import { AppConfig } from '@core_n/app.config';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from "@angular/core";

declare global {
  interface Window {
    flowplayer: any;
  }
}

@Component({
  selector: "app-flow-player",
  templateUrl: "./flow-player.component.html",
  styleUrls: ["./flow-player.component.scss"],
})
export class FlowPlayerComponent implements AfterViewInit, OnDestroy {
  @Input() sdpUrl = "";
  @Input() applicationName = "";
  @Input() streamName = "";
  @Input() secureToken?: any;
  @Input() enableDebugLogging = false;

  private readonly flowplayerToken = AppConfig.settings.videoAddress.videoPlayerInfo.flowToken;

  @ViewChild("playerHost", { static: true }) playerHost!: ElementRef<
    HTMLDivElement
  >;

  player: any = null;

  _statusText = "Idle";
  get statusText(): string {
    return this._statusText;
  }
  set statusText(value: string) {
    if (this._statusText !== value) {
      this._statusText = value;
      try {
        this.cdr.detectChanges();
      } catch {}
    }
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.createPlayer();
  }

  ngOnDestroy(): void {
    this.destroyPlayer();
  }

  private createPlayer(): void {
    this.destroyPlayer();

    if (!window.flowplayer) {
      this.statusText = "Flowplayer script is missing";
      console.error("window.flowplayer is not available");
      return;
    }

    if (!window.flowplayer.rts) {
      this.statusText = "Flowplayer RTS plugin is missing";
      console.error("window.flowplayer.rts is not available");
      return;
    }

    const source: any = {
      type: "wowza/webrtc",
      sdpUrl: this.sdpUrl,
      applicationName: this.applicationName,
      streamName: this.streamName,
    };

    if (this.secureToken != null) {
      source.secureToken = this.secureToken;
    }

    const config = {
      token: this.flowplayerToken,
      autoplay: true,
      muted: true,
      src: source,
    };

    this.log("Creating Flowplayer", config);

    try {
      this.player = window.flowplayer(this.playerHost.nativeElement, config);
      this.statusText = "Player created";

      this.attachPlayerEvents();
      this.attachRtsEvents();
      this.attachNativeVideoEventsLater();

      this.log("Flowplayer created successfully");
    } catch (error) {
      this.statusText = "Player creation failed";
      console.error("Flowplayer init exception", error);
    }
  }

  private destroyPlayer(): void {
    if (this.player) {
      try {
        if (typeof this.player.destroy === "function") {
          this.player.destroy();
        } else if (typeof this.player.pause === "function") {
          this.player.pause();
        }
      } catch (error) {
        console.warn("Failed to destroy player", error);
      }

      this.player = null;
    }

    if (this.playerHost?.nativeElement) {
      this.playerHost.nativeElement.innerHTML = "";
    }

    this.statusText = "Stopped";
  }

  private attachPlayerEvents(): void {
    if (!this.player) {
      return;
    }

    const playerEvents = [
      "ready",
      "loadstart",
      "loadedmetadata",
      "loadeddata",
      "canplay",
      "playing",
      "pause",
      "waiting",
      "stalled",
      "ended",
      "error",
    ];

    for (const eventName of playerEvents) {
      try {
        this.player.on(eventName, (event: any) => {
          this.log(`Player event: ${eventName}`, event);

          if (eventName === "playing") {
            this.statusText = "Playing";
          } else if (eventName === "waiting") {
            this.statusText = "Waiting";
          } else if (eventName === "loadedmetadata") {
            this.statusText = "Metadata loaded";
          } else if (eventName === "loadeddata") {
            this.statusText = "First frame loaded";
          } else if (eventName === "pause") {
            this.statusText = "Paused";
          } else if (eventName === "ended") {
            this.statusText = "Ended";
          } else if (eventName === "error") {
            this.statusText = "Error";
          }
        });
      } catch (error) {
        this.log(`Failed attaching player event: ${eventName}`, error);
      }
    }
  }

  private attachRtsEvents(): void {
    if (!this.player || !window.flowplayer?.rts?.events) {
      return;
    }

    const rtsEvents = window.flowplayer.rts.events;

    try {
      this.player.on(rtsEvents.STATUS, (event: any) => {
        const detail = event?.detail;
        this.log("RTS STATUS", detail);

        const status = detail?.status;
        if (status) {
          this.statusText = `RTS: ${status}`;
        }
      });
    } catch (error) {
      this.log("Failed attaching RTS STATUS", error);
    }

    try {
      this.player.on(rtsEvents.STATS, (event: any) => {
        this.log("RTS STATS", event?.detail);
      });
    } catch (error) {
      this.log("Failed attaching RTS STATS", error);
    }

    try {
      this.player.on(rtsEvents.VIEWER_COUNT, (event: any) => {
        this.log("RTS VIEWER_COUNT", event?.detail);
      });
    } catch (error) {
      this.log("Failed attaching RTS VIEWER_COUNT", error);
    }
  }

  private attachNativeVideoEventsLater(): void {
    setTimeout(() => {
      const video = this.playerHost?.nativeElement?.querySelector(
        "video"
      ) as HTMLVideoElement | null;

      if (!video) {
        this.log("No native video element found");
        return;
      }

      this.log("Native video found", {
        currentSrc: video.currentSrc,
        readyState: video.readyState,
        networkState: video.networkState,
        muted: video.muted,
        paused: video.paused,
        autoplay: video.autoplay,
      });

      const nativeEvents = [
        "loadstart",
        "loadedmetadata",
        "loadeddata",
        "canplay",
        "canplaythrough",
        "play",
        "playing",
        "waiting",
        "stalled",
        "suspend",
        "abort",
        "error",
      ];

      for (const eventName of nativeEvents) {
        video.addEventListener(eventName, () => {
          this.log(`Native video event: ${eventName}`, {
            readyState: video.readyState,
            networkState: video.networkState,
            currentTime: video.currentTime,
          });
        });
      }
    }, 1000);
  }

  private log(message: string, data?: any): void {
    if (!this.enableDebugLogging) {
      return;
    }

    if (data !== undefined) {
      console.log(`[FlowplayerVideo] ${message}`, data);
    } else {
      console.log(`[FlowplayerVideo] ${message}`);
    }
  }
}
